import dicomParser from "dicom-parser";
import { Decoder as JpegLosslessDecoder } from "jpeg-lossless-decoder-js";

const JPEG_BASELINE = new Set([
  "1.2.840.10008.1.2.4.50",
  "1.2.840.10008.1.2.4.51",
]);
const JPEG_LOSSLESS = new Set([
  "1.2.840.10008.1.2.4.57",
  "1.2.840.10008.1.2.4.70",
]);

const parser = dicomParser?.parseDicom
  ? dicomParser
  : (dicomParser?.default || dicomParser);

const samplesFromDecoded = (decoded, numBytes) => {
  if (!decoded) throw new Error("empty decode");
  if (decoded instanceof Uint8Array || decoded instanceof Uint16Array || decoded instanceof Int16Array) {
    return decoded;
  }
  if (decoded instanceof ArrayBuffer) {
    return numBytes === 1 ? new Uint8Array(decoded) : new Uint16Array(decoded);
  }
  if (decoded.buffer instanceof ArrayBuffer) {
    const { buffer, byteOffset = 0, byteLength = buffer.byteLength } = decoded;
    return numBytes === 1
      ? new Uint8Array(buffer, byteOffset, byteLength)
      : new Uint16Array(buffer, byteOffset, Math.floor(byteLength / 2));
  }
  throw new Error("unexpected decode output");
};

const cropToSingleFrame = (srcCanvas, cols, rows) => {
  const w = srcCanvas.width;
  const h = srcCanvas.height;
  if (!w || !h) return srcCanvas;

  let cropW = w;
  let cropH = h;
  if (cols && w >= cols * 1.8 && (!rows || Math.abs(h - rows) <= 2)) {
    cropW = cols;
  } else if (w >= h * 1.7) {
    cropW = Math.round(w / 2);
  }
  if (rows && h >= rows * 1.8 && (!cols || Math.abs(w - cols) <= 2 || cropW === cols)) {
    cropH = rows;
  }

  if (cropW === w && cropH === h) return srcCanvas;

  const canvas = document.createElement("canvas");
  canvas.width = cropW;
  canvas.height = cropH;
  const ctx = canvas.getContext("2d");
  if (!ctx) return srcCanvas;
  ctx.drawImage(srcCanvas, 0, 0, cropW, cropH, 0, 0, cropW, cropH);
  return canvas;
};

const scaleToThumb = (srcCanvas, thumbW = 256, thumbH = 256) => {
  const canvas = document.createElement("canvas");
  canvas.width = thumbW;
  canvas.height = thumbH;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("canvas unavailable");
  ctx.fillStyle = "#000";
  ctx.fillRect(0, 0, thumbW, thumbH);
  const scale = Math.max(thumbW / srcCanvas.width, thumbH / srcCanvas.height);
  const dw = srcCanvas.width * scale;
  const dh = srcCanvas.height * scale;
  ctx.drawImage(srcCanvas, (thumbW - dw) / 2, (thumbH - dh) / 2, dw, dh);
  return canvas.toDataURL("image/jpeg", 0.82);
};

const readFrameBytes = (dataSet) => {
  const pixelDataElement = dataSet.elements?.x7fe00010;
  if (!pixelDataElement) throw new Error("no pixel data");

  if (pixelDataElement.encapsulatedPixelData) {
    try {
      return parser.readEncapsulatedImageFrame(dataSet, pixelDataElement, 0);
    } catch (_) {
      return parser.readEncapsulatedPixelDataFromFragments(dataSet, pixelDataElement, 0, 1);
    }
  }

  const rows = dataSet.uint16("x00280010") || 0;
  const cols = dataSet.uint16("x00280011") || 0;
  const bits = dataSet.uint16("x00280100") || 16;
  const samples = dataSet.uint16("x00280002") || 1;
  const bytesPerSample = bits > 8 ? 2 : 1;
  const frameSize = rows * cols * samples * bytesPerSample;
  if (!rows || !cols || frameSize <= 0) throw new Error("invalid image size");
  return dataSet.byteArray.subarray(
    pixelDataElement.dataOffset,
    pixelDataElement.dataOffset + frameSize,
  );
};

const canvasFromJpegBytes = async (bytes) => {
  const copy = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength);
  const blob = new Blob([copy], { type: "image/jpeg" });
  const bitmap = await createImageBitmap(blob);
  const canvas = document.createElement("canvas");
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("canvas unavailable");
  ctx.drawImage(bitmap, 0, 0);
  bitmap.close?.();
  return canvas;
};

const canvasFromGrayPixels = (pixels, width, height, invert) => {
  const count = width * height;
  let min = Infinity;
  let max = -Infinity;
  const n = Math.min(pixels.length, count);
  for (let i = 0; i < n; i += 1) {
    const v = pixels[i];
    if (v < min) min = v;
    if (v > max) max = v;
  }
  const range = Math.max(1, max - min);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("canvas unavailable");
  const imageData = ctx.createImageData(width, height);
  const data = imageData.data;
  for (let i = 0; i < count; i += 1) {
    const src = i < pixels.length ? pixels[i] : min;
    let g = ((src - min) / range) * 255;
    if (invert) g = 255 - g;
    g = Math.max(0, Math.min(255, g));
    const o = i * 4;
    data[o] = g;
    data[o + 1] = g;
    data[o + 2] = g;
    data[o + 3] = 255;
  }
  ctx.putImageData(imageData, 0, 0);
  return canvas;
};

const pixelsFromUncompressed = (dataSet, frameBytes) => {
  const bits = dataSet.uint16("x00280100") || 8;
  const signed = dataSet.uint16("x00280103") === 1;
  if (bits <= 8) return frameBytes;
  const view = new DataView(frameBytes.buffer, frameBytes.byteOffset, frameBytes.byteLength);
  const count = Math.floor(frameBytes.byteLength / 2);
  const out = signed ? new Int16Array(count) : new Uint16Array(count);
  for (let i = 0; i < count; i += 1) {
    out[i] = signed ? view.getInt16(i * 2, true) : view.getUint16(i * 2, true);
  }
  return out;
};

/**
 * Render frame 0 of a DICOM ArrayBuffer to a small JPEG data URL.
 * Does not use DWV (safe alongside the main viewer).
 */
export const renderDicomFirstFrameThumb = async (arrayBuffer) => {
  if (!arrayBuffer || !parser?.parseDicom) throw new Error("dicom parser unavailable");
  const dataSet = parser.parseDicom(new Uint8Array(arrayBuffer));
  const rows = dataSet.uint16("x00280010");
  const cols = dataSet.uint16("x00280011");
  if (!rows || !cols) throw new Error("missing rows/cols");

  const ts = dataSet.string("x00020010") || "";
  const invert = String(dataSet.string("x00280004") || "").toUpperCase() === "MONOCHROME1";
  const frameBytes = readFrameBytes(dataSet);
  if (!frameBytes?.length) throw new Error("empty frame");

  let srcCanvas;
  if (JPEG_BASELINE.has(ts)) {
    srcCanvas = await canvasFromJpegBytes(frameBytes);
  } else if (JPEG_LOSSLESS.has(ts)) {
    const decoder = new JpegLosslessDecoder();
    const decoded = decoder.decode(frameBytes, 0, frameBytes.length);
    const numBytes = decoder.numBytes || 2;
    const width = decoder.xDim || cols;
    const height = decoder.yDim || rows;
    const samples = samplesFromDecoded(decoded, numBytes);
    srcCanvas = canvasFromGrayPixels(samples, width, height, invert);
  } else if (ts.startsWith("1.2.840.10008.1.2.4")) {
    throw new Error(`unsupported transfer syntax ${ts}`);
  } else {
    const pixels = pixelsFromUncompressed(dataSet, frameBytes);
    srcCanvas = canvasFromGrayPixels(pixels, cols, rows, invert);
  }

  srcCanvas = cropToSingleFrame(srcCanvas, cols, rows);
  return scaleToThumb(srcCanvas);
};
